












































































































































/* eslint-disable class-methods-use-this */

import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import { Vue, Component, Watch } from "vue-property-decorator";
import { TranslateResult } from "vue-i18n";
import debounce from "lodash.debounce";
import { authToken } from "@/utils/user";
import CdkFilterForm from "@/components/CdkFilterForm/CdkFilterForm.vue";
import CdkFilterFormProvider from "@/components/CdkFilterForm/CdkFilterFormProvider.vue";
import CdkNavigationProvider from "@/components/CdkNavigation/CdkNavigationProvider.vue";
import NotificationSettingsSwitch from "./NotificationSettingsSwitch.vue";
import DpaNotificationsAdDialogue from "@/components/DpaNotificationsAd/DpaNotificationsAdDialogue.vue";

interface Filter {
  color?: string;
  name?: string;
}

interface Confirmation {
  show: boolean;
  text: string;
  filter: any | null;
}

@Component({
  components: {
    DpaNotificationsAdDialogue,
    CdkNavigationProvider,
    CdkFilterFormProvider,
    CdkFilterForm,
    NotificationSettingsSwitch
  },
  computed: {
    ...mapState("stageconfig", ["config"]),
    ...mapState("filters", ["isSaved", "error"]),
    ...mapState("filters", {
      isLoading: ({ status }) => status === "loading"
    }),
    ...mapGetters("filters", [
      "getFilterById",
      "getNextFilterById",
      "getFirstFilter",
      "hasFilters"
    ]),
    ...mapGetters("filters", {
      filterNames: "getFilterNames"
    })
  },
  methods: {
    ...mapMutations("filters", [
      "setFilterIsVisible",
      "removeFilter",
      "setIsSaved"
    ]),
    ...mapActions("filters", [
      "redo",
      "undo",
      "updateFilter",
      "updateFilterIsVisible",
      "deleteFilter"
    ]),
    ...mapActions("settings", ["removeCombinedFilter"])
  }
})
export default class Settings extends Vue {
  deleteFilter: any;

  error: any;

  filterNames!: string[];

  getFilterById: any;

  getFirstFilter: any;

  getNextFilterById: any;

  initialFilter: Filter = {};

  isSaved?: boolean;

  nameRules: any[] = [];

  removeCombinedFilter: any;

  setIsSaved: any;

  settingsConfirmation: Confirmation = {
    show: false,
    text: "",
    filter: null
  };

  showDpaIdAppAd: boolean = false;

  undo: any;

  updateFilterIsVisible: any;

  $route: any;

  $router: any;

  /**
   * Returns the current filter
   * @returns {String}
   */
  get filterId() {
    return this.$route.query.filterId;
  }

  get filter() {
    return this.getFilterById(this.filterId);
  }

  get settingsSaved() {
    return this.isSaved;
  }

  set settingsSaved(value) {
    this.setIsSaved(value);
  }

  get visibility() {
    return this.filter && this.filter.isVisible;
  }

  set visibility(value) {
    this.updateFilterIsVisible({
      id: this.filterId,
      isVisible: value,
      authJWT: authToken(this)
    });
  }

  /**
   * Returns a compound string for the visiblity settings
   * @return {String}
   */
  get visibilityLabel() {
    if (this.filter) {
      return `"${this.filter.name}" ${this.$t("showInMenu")}`;
    }
    return "";
  }

  /**
   * Returns a compound string for the notification settings
   * @return {String}
   */
  get emailNotificationsLabel(): TranslateResult {
    return this.$t("byEmail");
  }

  get pushNotificationsLabel(): TranslateResult {
    return this.$t("getNotifications");
  }

  get isFilterFormValid(): boolean {
    if (this.initialFilter && this.initialFilter.color) {
      return this.isNewFilterNameValid && this.initialFilter.color.length > 0;
    }
    return false;
  }

  get updateNameNoConflict(): boolean {
    return (
      this.filterNames.filter(name => name === this.initialFilter.name)
        .length <= 1
    );
  }

  get isNewFilterNameValid(): boolean {
    if (this.initialFilter && this.initialFilter.name) {
      return this.initialFilter.name.length > 0 && this.updateNameNoConflict;
    }
    return false;
  }

  /**
   * Removes the current filter and navigates to the next available filter.
   */
  async removeFilterAndNavigate({ filterId }) {
    const removedFilter = { ...this.filter };
    this.settingsConfirmation.filter = removedFilter;

    if (filterId === this.filterId) {
      const nextFilter = this.getNextFilterById(filterId);

      if (!this.error && nextFilter && nextFilter.id !== this.filterId) {
        const { id } = nextFilter;
        this.$router.push({ query: { filterId: id } });
      } else {
        this.$router.push({ query: {} });
      }
    }

    await this.deleteFilter({
      filter: removedFilter,
      authJWT: authToken(this)
    });

    // TODO: Must remove filter from filterCombination if it is currently selected
    await this.removeCombinedFilter({
      filter: removedFilter,
      authJWT: authToken(this)
    });

    this.settingsConfirmation.text = this.error
      ? "Entschuldigung, ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut"
      : `Die Suche "${removedFilter.name}" wurde gelöscht`;
    this.settingsConfirmation.show = true;
  }

  /*
   * Invokes undo method and navigates back to the previously removed filter.
   * Hides the undo dialog.
   */
  undoAndNavigate(): void {
    this.undo();

    const removedFilter = { ...this.settingsConfirmation.filter };

    if (removedFilter && removedFilter.id) {
      const { id } = removedFilter;
      this.$router.push({ query: { filterId: id } });
      this.settingsConfirmation.show = false;
      this.settingsConfirmation.filter = null;
    }
  }

  newFilterNameNoConflict(newFilterName): boolean {
    // return this.filterNames.filter(name => name === newFilterName).length === 0;
    return this.filter.name !== newFilterName;
  }

  /*
   * Create a fresh copy of the current filter so that we don't mutate its
   * props directly when passing them to the form.
   */
  cloneFilter(): void {
    this.initialFilter = {
      ...this.filter
    };
  }

  nameHasNoConflict(name): boolean {
    return (
      name &&
      this.filterNames.filter(filterName => filterName === name).length <=
        (this.filter.name === name ? 1 : 0)
    );
  }

  @Watch("filter")
  onFilterChanged() {
    this.cloneFilter();
  }

  /*
   * Merges the payload with the initialFilter. Payload is an object.
   */
  handleFormChange = debounce(function onFormChange(payload) {
    const filter = {
      // @ts-ignore
      ...this.initialFilter,
      ...payload
    };

    // NB: Prevent a conflicting filtername from being updated in the store
    // the second boolean in nameRules array is dynamically added/removed
    // if the name is or isn't conflicting
    // @ts-ignore
    if (this.nameHasNoConflict(filter.name)) {
      // remove boolean
      // @ts-ignore
      this.nameRules.splice(1, 1);
      // @ts-ignore
      this.updateFilter({ filter, authJWT: authToken(this) });
    } else {
      // add boolean
      // @ts-ignore
      this.nameRules.splice(1, 1, this.nameHasNoConflict(filter.name));
    }
  }, 500);

  /**
   * Hide the confirmation dialog and
   * if there a zero filters left after deleting a filter,
   * redirect the user back to the default view.
   */
  handleConfirmationOkClick(): void {
    this.settingsConfirmation.show = false;

    const hasNextFilter: boolean =
      this.getNextFilterById(this.filterId) !== undefined;

    if (!hasNextFilter) {
      this.$router.push({ path: "/" });
    }
  }

  hasFilters: boolean;

  created(): void {
    this.cloneFilter();
    this.nameRules.splice(0, 1, this.updateNameNoConflict);

    if (!this.hasFilters) {
      this.$router.push({
        path: "/settings/language"
      });
    }

    if (!this.filterId) {
      const firstFilter = this.getFirstFilter;

      if (firstFilter) {
        this.$router.push({
          query: {
            filterId: firstFilter.id
          }
        });
      }
    }
  }

  mounted() {
    const switches = Array.from(document.querySelectorAll(".Settings-switch"));
    switches.forEach(s => {
      const label = s.querySelector("label");
      const height = label ? label.getBoundingClientRect().height : 0;
      s.setAttribute("style", `height: ${height + (height / 24) * 10}px`);
    });
  }
}
